import { RefreshOutlined } from '@mui/icons-material';
import { IconButton, Stack } from '@mui/material';
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FilterType, IReportFilter } from '../models/report-filter.model';
import {
    IRunReportDateFilter,
    IRunReportFilter,
    IRunReportListFilter,
    IRunReportNumberFilter,
    IRunReportSearchFilter,
    IRunReportStringFilter,
} from '../models/run-report.model';
import {
    ControlledDatePicker,
    ControlledInput,
    ControlledNumberInput,
    FormGrid,
    Options,
    OptionsToggle,
} from '../../shared';
import { ReportSelectFilter } from './report-select-filter.component';
import { allRequiredFiltersFilledInCheck } from '../utils/all-required-filters-filled-in-check.util';

interface Props {
    availableFilters: IReportFilter[];
    onChange: (filters: IRunReportFilter[]) => void;
    filters: IRunReportFilter[];
}

type FilterValue = string[] | { startDate?: string; endDate?: string } | { min?: string; max?: string } | string;
type DashboardFilterForm = Record<string, FilterValue>;

const DOT_CHARACTER = '__DOT__';

export const ReportFilter: FC<Props> = ({ availableFilters, filters, onChange }) => {
    const { t } = useTranslation();
    const form = useForm();
    const hasFilterFormChanged = useHasFilterFormChanged(form, filters, availableFilters);
    const areAllRequiredFiltersFilledIn = useHasAllRequiredFiltersFilledIn(form, availableFilters);

    const [options, setOptions] = useState<Options>({});

    const onFilterChange = useCallback(
        (formValues: DashboardFilterForm) => {
            onChange(mapFormValuesToAppliedFilters(formValues, availableFilters));
        },
        [onChange, availableFilters],
    );

    useEffect(() => {
        const inactiveFilters: string[] = [];
        Object.entries(options)?.forEach(([, option]) => {
            const filter = availableFilters?.find((filter) => filter.label === option.label);

            if (!option.active && filter) {
                const { type, column } = filter;
                if (type === FilterType.SELECT || type === FilterType.SINGLE_SELECT || type === FilterType.SEARCH)
                    return inactiveFilters.push(column);
                if (type === FilterType.DATE) return inactiveFilters.push(`${column}.startDate`, `${column}.endDate`);
                if (type === FilterType.NUMBER) return inactiveFilters.push(`${column}.min`, `${column}.max`);
            }
        });

        if (inactiveFilters?.length) inactiveFilters?.forEach((field) => form.resetField(field));
    }, [options, availableFilters, form]);

    useEffect(() => {
        form.reset({});
        filters.forEach((filter) => {
            const fieldName = getFieldNameFromColumn(filter.column);
            if (filter.type === FilterType.NUMBER) {
                form.setValue(`${fieldName}.min`, filter.min?.toString());
                form.setValue(`${fieldName}.max`, filter.max?.toString());
            }
            if (filter.type === FilterType.DATE) {
                form.setValue(`${fieldName}.startDate`, filter.start);
                form.setValue(`${fieldName}.endDate`, filter.end);
            }
            if (filter.type === FilterType.SELECT) {
                form.setValue(fieldName, filter.values);
            }
            if (filter.type === FilterType.SINGLE_SELECT || filter.type === FilterType.SEARCH) {
                form.setValue(fieldName, filter.value);
            }
        });
    }, [filters, form]);

    useEffect(() => {
        setOptions({
            filter: { type: 'title', label: t('filter') },
            ...mapAvailableFilterToFilterOptions(availableFilters),
        });
    }, [availableFilters, t]);

    const hasOptionalFilter = availableFilters?.some((filter) => !filter.required);

    return (
        <FormProvider {...form}>
            <Stack direction="row" spacing={1}>
                {hasOptionalFilter && <OptionsToggle options={options} onChange={setOptions} />}
                <FormGrid containerProps={{ spacing: 1, rowGap: 2, alignItems: 'center' }}>
                    {availableFilters.map((filter) => {
                        if (!options[filter.label]?.active && !filter.required) return <Fragment key={filter.label} />;

                        const fieldName = getFieldNameFromColumn(filter.column);
                        return (
                            <Fragment key={filter.label}>
                                {filter.type === FilterType.NUMBER && (
                                    <Stack direction="row" spacing={1}>
                                        <ControlledNumberInput
                                            name={`${fieldName}.min`}
                                            label={`${t('min')} ${filter.label.toLowerCase()}`}
                                            size="small"
                                            decimalScale={0}
                                            className="filter"
                                        />
                                        <ControlledNumberInput
                                            name={`${fieldName}.max`}
                                            label={`${t('max')} ${filter.label.toLowerCase()}`}
                                            size="small"
                                            decimalScale={0}
                                            className="filter"
                                        />
                                    </Stack>
                                )}
                                {filter.type === FilterType.DATE && (
                                    <Stack direction="row" spacing={1}>
                                        <ControlledDatePicker
                                            name={`${fieldName}.startDate`}
                                            label={t('startDate')}
                                            size="small"
                                            className="filter"
                                        />
                                        <ControlledDatePicker
                                            name={`${fieldName}.endDate`}
                                            label={t('endDate')}
                                            size="small"
                                            className="filter"
                                        />
                                    </Stack>
                                )}
                                {filter.type === FilterType.SELECT && (
                                    <ReportSelectFilter
                                        name={fieldName}
                                        label={filter.label}
                                        options={filter.values || []}
                                        size="small"
                                    />
                                )}
                                {filter.type === FilterType.SINGLE_SELECT && (
                                    <ReportSelectFilter
                                        name={fieldName}
                                        label={filter.label}
                                        options={filter.values || []}
                                        multiple={false}
                                        size="small"
                                    />
                                )}
                                {filter.type === FilterType.SEARCH && (
                                    <ControlledInput
                                        name={fieldName}
                                        label={filter.label}
                                        placeholder={t('search')}
                                        size="small"
                                    />
                                )}
                            </Fragment>
                        );
                    })}

                    {hasFilterFormChanged && areAllRequiredFiltersFilledIn && (
                        <IconButton onClick={form.handleSubmit(onFilterChange)}>
                            <RefreshOutlined color="primary" />
                        </IconButton>
                    )}
                </FormGrid>
            </Stack>
        </FormProvider>
    );
};

function useHasAllRequiredFiltersFilledIn(
    form: UseFormReturn<DashboardFilterForm>,
    availableFilters: IReportFilter[],
): boolean {
    const formValues = form.watch();

    return useMemo(() => {
        const filters = mapFormValuesToAppliedFilters(formValues, availableFilters);

        return allRequiredFiltersFilledInCheck(availableFilters, filters);
    }, [formValues, availableFilters]);
}

function useHasFilterFormChanged(
    form: UseFormReturn<DashboardFilterForm>,
    appliedFilters: IRunReportFilter[],
    availableFilters: IReportFilter[],
): boolean {
    const formValues = form.watch();

    return useMemo(() => {
        const newlyAppliedFilters = mapFormValuesToAppliedFilters(formValues, availableFilters);
        return JSON.stringify(newlyAppliedFilters) !== JSON.stringify(appliedFilters);
    }, [appliedFilters, availableFilters, formValues]);
}

function mapFormValuesToAppliedFilters(
    filterForm: DashboardFilterForm,
    availableFilters: IReportFilter[],
): IRunReportFilter[] {
    return Object.entries(filterForm)
        .map(([fieldName, filterValue]) => mapFilter(getColumnFromFieldName(fieldName), filterValue, availableFilters))
        .filter((appliedFilter) => !!appliedFilter) as IRunReportFilter[];
}

function mapFilter(
    column: string,
    value: FilterValue,
    availableFilters: IReportFilter[],
):
    | IRunReportNumberFilter
    | IRunReportDateFilter
    | IRunReportListFilter
    | IRunReportStringFilter
    | IRunReportSearchFilter
    | undefined {
    switch (availableFilters.find((filter) => filter.column === column)?.type) {
        case FilterType.SELECT:
            return { type: FilterType.SELECT, column, values: (value as string[]) || [] };
        case FilterType.SINGLE_SELECT:
            return { type: FilterType.SINGLE_SELECT, column, value: value as string };
        case FilterType.SEARCH:
            return { type: FilterType.SEARCH, column, value: (value as string)?.replaceAll(/  +/g, ' ').slice(0, 200) };
        case FilterType.DATE: {
            const { startDate, endDate } = value as { startDate: string; endDate: string };
            return { type: FilterType.DATE, column, start: startDate, end: endDate };
        }
        case FilterType.NUMBER: {
            const { min, max } = value as { min: string; max: string };
            return {
                type: FilterType.NUMBER,
                column,
                ...(min?.toString() !== '' && { min: Number(min) }),
                ...(max?.toString() !== '' && { max: Number(max) }),
            };
        }
        default:
            return;
    }
}

function mapAvailableFilterToFilterOptions(availableFilters: IReportFilter[]) {
    return availableFilters
        .filter(({ required }) => !required)
        .reduce((filters, { label }) => ({ ...filters, [label]: { active: false, label: label } }), {});
}

function getFieldNameFromColumn(column: string): string {
    return column.replace('.', DOT_CHARACTER);
}

function getColumnFromFieldName(fieldName: string): string {
    return fieldName.replace(DOT_CHARACTER, '.');
}
